import { useState } from 'react';
import type { ProjectSearchResult } from '@/api/planning';
import type { AddMinprijsLine } from '@/api/onderaannemersPoEdit';
import { AddLineModalShell } from './AddLineModalShell';
import { ProjectPicker } from './ProjectPicker';

interface AddMinprijsModalProps {
  onAdd: (payload: AddMinprijsLine) => Promise<void>;
  onClose: () => void;
}

/** Add a negative "Minprijs reeds geplaatste collectoren (N)" correction line. */
export function AddMinprijsModal({ onAdd, onClose }: AddMinprijsModalProps) {
  const [project, setProject] = useState<ProjectSearchResult | null>(null);
  const [count, setCount] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedCount = Number.parseInt(count, 10);
  const countOk = Number.isFinite(parsedCount) && parsedCount > 0;
  const canSubmit = !!project && countOk;

  const handleSubmit = async () => {
    if (!canSubmit || !project || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onAdd({ kind: 'minprijs', projectId: project.id, count: parsedCount });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Minprijs toevoegen mislukt');
      setSubmitting(false);
    }
  };

  return (
    <AddLineModalShell
      title="Minprijs toevoegen"
      submitLabel="Toevoegen"
      submitting={submitting}
      canSubmit={canSubmit}
      error={error}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <div>
        <label className="block text-sm font-semibold text-td-navy mb-1">Project</label>
        <ProjectPicker selected={project} onSelect={setProject} autoFocus />
      </div>
      <div>
        <label htmlFor="add-minprijs-count" className="block text-sm font-semibold text-td-navy mb-1">
          Aantal reeds geplaatste collectoren
        </label>
        <input
          id="add-minprijs-count"
          type="text"
          inputMode="numeric"
          value={count}
          onChange={(e) => setCount(e.target.value)}
          className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-td-blue"
        />
      </div>
      {countOk && (
        <p className="text-xs text-td-muted">
          Voegt <span className="font-semibold text-td-navy">Minprijs reeds geplaatste collectoren ({parsedCount})</span> toe (negatieve correctie).
        </p>
      )}
    </AddLineModalShell>
  );
}
